import {
  ApiError,
  identifierRejectionReason,
  isUnitTracked,
  type Order,
  type ProductType,
  queryKeys,
  UNIT_IDENTIFIER_LABELS,
  unitIdentifierKind,
} from '@mevaser/shared'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

type OrderLine = Order['items'][number]

/** Candidates are pasted one per line or comma separated — split on anything that isn't part of an identifier. */
export function parseCandidates(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).filter(Boolean))]
}

/** What kind of identifier a line's candidates are, for labels and checking. */
export function candidateLabel(type: ProductType): string {
  return isUnitTracked(type) ? UNIT_IDENTIFIER_LABELS[unitIdentifierKind(type)] : 'SKU'
}

/** Candidates that cannot be real identifiers for this product (only checked for IMEI / SIM lines). */
export function badCandidates(type: ProductType, candidates: string[]): string[] {
  if (!isUnitTracked(type)) return []
  const kind = unitIdentifierKind(type)
  return candidates.filter((c) => identifierRejectionReason(kind, c) !== null)
}

/**
 * The candidates / pool / note inputs, shared by this dialog and the new-order
 * line so a flag reads the same whether it is raised at the sale or after it.
 */
export function IdentityFlagFields({
  productType,
  candidatesText,
  onCandidatesText,
  pool,
  onPool,
  note,
  onNote,
}: {
  productType: ProductType
  candidatesText: string
  onCandidatesText: (v: string) => void
  pool: string
  onPool: (v: string) => void
  note?: string
  onNote?: (v: string) => void
}) {
  const label = candidateLabel(productType)
  const parsed = parseCandidates(candidatesText)
  const bad = badCandidates(productType, parsed)
  return (
    <div className="space-y-3">
      <Field
        label={`Possible ${label}s (optional)`}
        hint="Paste or type the ones it might be — one per line or separated by commas."
        error={
          bad.length > 0
            ? `Not a valid ${label}: ${bad.slice(0, 3).join(', ')}${bad.length > 3 ? '…' : ''}`
            : undefined
        }
      >
        {(props) => (
          <Textarea
            {...props}
            rows={3}
            className="font-identifier"
            value={candidatesText}
            onChange={(e) => onCandidatesText(e.target.value)}
          />
        )}
      </Field>
      <Field
        label="Pool / batch it came from (optional)"
        hint="For example a range, box or delivery."
      >
        {(props) => <Input {...props} value={pool} onChange={(e) => onPool(e.target.value)} />}
      </Field>
      {onNote ? (
        <Field label="Note (optional)">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={note ?? ''}
              onChange={(e) => onNote(e.target.value)}
            />
          )}
        </Field>
      ) : null}
    </div>
  )
}

/** Only phones, SIMs and counted stock can be flagged, and not on a dead order. */
export function canFlagLine(order: Order, item: OrderLine): boolean {
  return (
    (isUnitTracked(item.productType) || item.productType === 'stock') &&
    order.status !== 'cancelled' &&
    order.status !== 'refunded'
  )
}

/**
 * t1137: flag a sold line as possibly the wrong unit (or edit the flag), and
 * later confirm which unit it really was.
 */
export function OrderIdentityFlagDialog({
  order,
  item,
  mode,
  onClose,
}: {
  order: Order
  item: OrderLine | null
  mode: 'flag' | 'confirm'
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [candidatesText, setCandidatesText] = useState('')
  const [pool, setPool] = useState('')
  const [note, setNote] = useState('')
  const [actual, setActual] = useState('')

  useEffect(() => {
    if (!item) return
    const flag = item.identityFlag
    setCandidatesText(flag && !flag.resolvedAt ? flag.candidates.join('\n') : '')
    setPool(flag && !flag.resolvedAt ? (flag.pool ?? '') : '')
    setNote(flag && !flag.resolvedAt && mode === 'flag' ? (flag.note ?? '') : '')
    setActual('')
  }, [item, mode])

  const onDone = async (title: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() }),
    ])
    toast.toast({ tone: 'success', title })
    onClose()
  }
  const onFail = (title: string) => (error: unknown) =>
    toast.error(title, error instanceof ApiError ? error.message : undefined)

  const flagLine = useMutation({
    mutationFn: () =>
      api.put(`/orders/${order.id}/items/${item?.id}/identity-flag`, {
        candidates: parseCandidates(candidatesText),
        pool: pool.trim() || null,
        note: note.trim() || null,
      }),
    onSuccess: () => onDone('Marked as unverified'),
    onError: onFail('Could not flag this item'),
  })

  const confirmLine = useMutation({
    mutationFn: () =>
      api.post<{ swapped: boolean }>(
        `/orders/${order.id}/items/${item?.id}/identity-flag/resolve`,
        { actualIdentifier: actual.trim() || null, note: note.trim() || null },
      ),
    onSuccess: (result) =>
      onDone(result.swapped ? 'Unit corrected — stock updated' : 'Unit confirmed'),
    onError: onFail('Could not confirm this item'),
  })

  if (!item) return null
  const label = candidateLabel(item.productType)
  const tracked = isUnitTracked(item.productType)
  const flag = item.identityFlag
  const candidates = flag?.candidates ?? []
  const badActual = actual.trim() && tracked ? badCandidates(item.productType, [actual.trim()]) : []
  const busy = flagLine.isPending || confirmLine.isPending

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'flag'
              ? flag && !flag.resolvedAt
                ? 'Edit unverified item'
                : 'Mark as possibly wrong'
              : 'Confirm the real item'}{' '}
            — {item.productName}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {mode === 'flag' ? (
            <>
              <Alert tone="info">
                The item stays deducted from stock
                {item.imei ? ` as ${item.imei}` : ''}. This only records that the exact {label} may
                not be right, so it can be corrected later.
              </Alert>
              <IdentityFlagFields
                productType={item.productType}
                candidatesText={candidatesText}
                onCandidatesText={setCandidatesText}
                pool={pool}
                onPool={setPool}
                note={note}
                onNote={setNote}
              />
            </>
          ) : (
            <>
              {tracked ? (
                <>
                  <Alert tone="info">
                    Recorded as <span className="font-identifier">{item.imei}</span>. If it was
                    really a different one, enter it — the two are swapped so stock stays right.
                    Leave it empty if the recorded one was correct.
                  </Alert>
                  {candidates.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {candidates.map((c) => (
                        <Button
                          key={c}
                          type="button"
                          size="sm"
                          variant={actual === c ? 'primary' : 'secondary'}
                          className="font-identifier"
                          onClick={() => setActual(c)}
                        >
                          {c}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                  <Field
                    label={`Actual ${label}`}
                    error={badActual.length > 0 ? `Not a valid ${label}` : undefined}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        className="font-identifier"
                        value={actual}
                        onChange={(e) => setActual(e.target.value)}
                      />
                    )}
                  </Field>
                </>
              ) : (
                <Alert tone="info">
                  Counted stock isn't tied to one unit, so this just closes the flag.
                </Alert>
              )}
              <Field label="Note (optional)">
                {(props) => (
                  <Textarea
                    {...props}
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
              </Field>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {mode === 'flag' ? (
            <Button
              onClick={() => flagLine.mutate()}
              loading={flagLine.isPending}
              disabled={badCandidates(item.productType, parseCandidates(candidatesText)).length > 0}
            >
              Save
            </Button>
          ) : (
            <Button
              onClick={() => confirmLine.mutate()}
              loading={confirmLine.isPending}
              disabled={badActual.length > 0}
            >
              {actual.trim() ? 'Correct item' : 'Confirm item'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
